import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { Account } from "@/types/account";
import { accountService, type UpdateAccountPayload } from "@/lib/api-services/account-service";

interface AccountState {
  account: Account | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  fetchAccount: (accountId: string) => Promise<void>;
  updateAccount: (accountId: string, data: UpdateAccountPayload) => Promise<void>;
  reset: () => void;
}

export const useAccountStore = create<AccountState>()(
  immer((set) => ({
    account: null,
    isLoading: false,
    isSaving: false,
    error: null,

    fetchAccount: async (accountId) => {
      set((state) => { state.isLoading = true; state.error = null; });
      try {
        const account = await accountService.getAccount(accountId);
        set((state) => { state.account = account; state.isLoading = false; });
      } catch (err) {
        set((state) => {
          state.isLoading = false;
          state.error = err instanceof Error ? err.message : "Failed to load account";
        });
      }
    },

    updateAccount: async (accountId, data) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const updated = await accountService.updateAccount(accountId, data);
        set((state) => { state.account = updated; state.isSaving = false; });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to update account";
        });
        throw err;
      }
    },

    reset: () => {
      set((state) => {
        state.account = null;
        state.isLoading = false;
        state.isSaving = false;
        state.error = null;
      });
    },
  })),
);
